import type { AnalyticsEvent, BaseEventParams } from './types'

// Extend the Window interface to include dataLayer
export interface WindowWithDataLayer extends Window {
  dataLayer: Record<string, unknown>[]
}

let _appName = 'unknown'
let _product = 'unknown'
let _debug = false

function getWindow(): WindowWithDataLayer | undefined {
  if (typeof window !== 'undefined') {
    return window as unknown as WindowWithDataLayer
  }
  return undefined
}

/**
 * Initialize the analytics module. Called once by AnalyticsProvider on mount.
 */
export function initAnalytics(appName: string, product = 'unknown', debug = false) {
  _appName = appName
  _product = product
  _debug = debug

  const win = getWindow()
  if (win) {
    win.dataLayer = win.dataLayer || []
  }
}

/**
 * Push arbitrary data to the GTM dataLayer.
 */
export function pushToDataLayer(data: Record<string, unknown>) {
  const win = getWindow()
  if (!win) return

  win.dataLayer = win.dataLayer || []
  win.dataLayer.push(data)

  if (_debug) {
    console.log('[Analytics] dataLayer.push:', data)
  }
}

/**
 * Track a typed analytics event by pushing it to the GTM dataLayer. GTM's
 * generic `GA4 - event` tag (Event Name = {{Event}}) forwards it and its
 * params to GA4. Auto-injects `app_origin`, `product`, `page_path`, and
 * `page_title`; every event-specific param rides along.
 *
 * NOTE: in a GTM-managed container, GA4 sends happen through GTM tags — a raw
 * `gtag('event', …)` call does NOT reach GA4 here. So events go via the
 * dataLayer, and the generic GTM tag does the sending. See README.
 */
export function trackEvent(event: AnalyticsEvent) {
  const baseParams: BaseEventParams = {
    app_origin: _appName,
    product: _product,
    page_path:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    page_title:
      typeof document !== 'undefined' ? document.title : undefined,
  }

  pushToDataLayer({
    event: event.name,
    ...baseParams,
    ...event.params,
  })
}

/**
 * Track a virtual page view (SPA route change).
 */
export function trackPageView(url: string, title?: string) {
  pushToDataLayer({
    event: 'page_view',
    app_origin: _appName,
    product: _product,
    page_path: url,
    page_title:
      title ||
      (typeof document !== 'undefined' ? document.title : ''),
  })
}

/**
 * Returns the current app name used for `app_origin`.
 */
export function getAppName() {
  return _appName
}
